import * as THREE from 'three';
import { CityState, Coordinate } from '../simulation/types';
import { AssetManager } from './AssetManager';

interface VehicleAgent {
  mesh: THREE.Object3D;
  path: Coordinate[];
  currentPathIndex: number;
  worldX: number;
  worldZ: number;
  speed: number;
}

interface PedestrianAgent {
  mesh: THREE.Object3D;
  roadX: number;
  roadY: number;
  offsetX: number;
  offsetZ: number;
  dir: number;
  speed: number;
  legL: THREE.Object3D | null;
  legR: THREE.Object3D | null;
}

export class LifeRenderer {
  private scene: THREE.Scene;
  private vehicles: VehicleAgent[] = [];
  private pedestrians: PedestrianAgent[] = [];

  private readonly maxVehicles = 12;
  private readonly maxPedestrians = 16;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public update(state: CityState, roadEngine: any, delta: number) {
    const roadKeys = Object.keys(state.roads);
    if (roadKeys.length === 0) {
      this.clear();
      return;
    }

    const time = performance.now() * 0.001;

    this.updateVehicles(state, delta);
    this.updatePedestrians(state, delta, time);

    // Spawning ticks based on random probabilities
    if (this.vehicles.length < this.maxVehicles && roadKeys.length >= 2 && Math.random() < 0.05) {
      this.spawnVehicle(roadKeys, roadEngine, state);
    }
    if (this.pedestrians.length < this.maxPedestrians && roadKeys.length >= 1 && Math.random() < 0.08) {
      this.spawnPedestrian(roadKeys, state);
    }
  }

  private updateVehicles(state: CityState, delta: number) {
    const halfWidth = state.width / 2;
    const halfHeight = state.height / 2;

    for (let i = this.vehicles.length - 1; i >= 0; i--) {
      const v = this.vehicles[i];
      const targetCoord = v.path[v.currentPathIndex];

      // Convert grid node to 3D world coords
      const targetWorldX = targetCoord.x - halfWidth + 0.5;
      const targetWorldZ = targetCoord.y - halfHeight + 0.5;

      const dx = targetWorldX - v.worldX;
      const dz = targetWorldZ - v.worldZ;
      const dist = Math.sqrt(dx * dx + dz * dz);

      // Speed multiplied by game tick speed rate & time delta
      const currentSpeed = v.speed * (state.simSpeed || 1) * delta;

      if (dist < 0.05) {
        v.currentPathIndex++;
        if (v.currentPathIndex >= v.path.length) {
          // Despawn
          this.scene.remove(v.mesh);
          this.vehicles.splice(i, 1);
          continue;
        }
      } else {
        const step = Math.min(currentSpeed, dist);
        const moveX = (dx / dist) * step;
        const moveZ = (dz / dist) * step;

        v.worldX += moveX;
        v.worldZ += moveZ;

        v.mesh.position.set(v.worldX, 0, v.worldZ);

        // Turn vehicle to face next node beautifully!
        // We look at targetWorldX, targetWorldZ
        const lookTarget = new THREE.Vector3(targetWorldX, 0, targetWorldZ);
        v.mesh.lookAt(lookTarget);
      }
    }
  }

  private updatePedestrians(state: CityState, delta: number, time: number) {
    const halfWidth = state.width / 2;
    const halfHeight = state.height / 2;

    for (let i = this.pedestrians.length - 1; i >= 0; i--) {
      const p = this.pedestrians[i];
      const key = `${p.roadX},${p.roadY}`;

      // If the road has been demolished under the pedestrian, remove them immediately
      if (!state.roads[key]) {
        this.scene.remove(p.mesh);
        this.pedestrians.splice(i, 1);
        continue;
      }

      // Walk back and forth along the sidewalk of a single tile
      const currentSpeed = p.speed * (state.simSpeed || 1) * delta;
      p.offsetX += p.dir * currentSpeed;

      if (Math.abs(p.offsetX) > 0.42) {
        p.dir *= -1;
        p.mesh.rotation.y = p.dir > 0 ? 0 : Math.PI; // Face the movement direction
      }

      const worldX = p.roadX - halfWidth + 0.5;
      const worldZ = p.roadY - halfHeight + 0.5;

      // Bobbing character height + leg swing animation for cozy walking look!
      const bobHeight = Math.abs(Math.sin(time * 8)) * 0.03;
      p.mesh.position.set(worldX + p.offsetX, bobHeight, worldZ + p.offsetZ);

      // Leg swinging rotations
      if (p.legL && p.legR) {
        p.legL.rotation.x = Math.sin(time * 12) * 0.45;
        p.legR.rotation.x = -Math.sin(time * 12) * 0.45;
      }
    }
  }

  private spawnVehicle(roadKeys: string[], roadEngine: any, state: CityState) {
    const startKey = roadKeys[Math.floor(Math.random() * roadKeys.length)];
    const endKey = roadKeys[Math.floor(Math.random() * roadKeys.length)];

    if (startKey === endKey) return;

    const [sx, sy] = startKey.split(',').map(Number);
    const [ex, ey] = endKey.split(',').map(Number);

    const path = roadEngine.findPath(sx, sy, ex, ey);
    if (!path || path.length < 2) return;

    // Standard low-poly color themes (Vibrant sports car look)
    const carColors = [0xff1744, 0x2979ff, 0x00e676, 0xffeb3b, 0xff9100, 0xd500f9, 0xffffff, 0x111111];
    const color = carColors[Math.floor(Math.random() * carColors.length)];

    const assetMgr = AssetManager.getInstance();
    const mesh = assetMgr.getVehicleMesh(color);

    const halfWidth = state.width / 2;
    const halfHeight = state.height / 2;

    const startWorldX = sx - halfWidth + 0.5;
    const startWorldZ = sy - halfHeight + 0.5;

    mesh.position.set(startWorldX, 0, startWorldZ);
    this.scene.add(mesh);

    this.vehicles.push({
      mesh,
      path,
      currentPathIndex: 1,
      worldX: startWorldX,
      worldZ: startWorldZ,
      speed: 1.2 + Math.random() * 0.6
    });
  }

  private spawnPedestrian(roadKeys: string[], state: CityState) {
    const roadKey = roadKeys[Math.floor(Math.random() * roadKeys.length)];
    const [rx, ry] = roadKey.split(',').map(Number);

    const assetMgr = AssetManager.getInstance();
    const mesh = assetMgr.getPedestrianMesh();

    const halfWidth = state.width / 2;
    const halfHeight = state.height / 2;

    // Offset sideways to place them onto sidewalks rather than middle of street lanes
    // Sidewalk is around Z = +/- 0.47 or X = +/- 0.47
    // Let's place them on a random sidewalk side:
    const offsetZ = Math.random() > 0.5 ? 0.43 : -0.47;

    const startWorldX = rx - halfWidth + 0.5;
    const startWorldZ = ry - halfHeight + 0.5;

    mesh.position.set(startWorldX, 0, startWorldZ + offsetZ);
    this.scene.add(mesh);

    // Retrieve child legs of block citizen so we can animate their rotation
    const legL = mesh.getObjectByName('legL') || null;
    const legR = mesh.getObjectByName('legR') || null;

    this.pedestrians.push({
      mesh,
      roadX: rx,
      roadY: ry,
      offsetX: -0.4 + Math.random() * 0.8,
      offsetZ,
      dir: Math.random() > 0.5 ? 1 : -1,
      speed: 0.2 + Math.random() * 0.15,
      legL,
      legR
    });
  }

  public clear() {
    this.vehicles.forEach((v) => this.scene.remove(v.mesh));
    this.vehicles = [];

    this.pedestrians.forEach((p) => this.scene.remove(p.mesh));
    this.pedestrians = [];
  }
}
